import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { Sun, Moon, Menu, X, LogOut } from 'lucide-react';

export default function Navigation() {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [showMenu, setShowMenu] = useState(false);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleLogout = async () => {
    await signOut();
    setShowMenu(false);
    navigate('/');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setShowMenu(false);
  };

  const getUserInitials = () => {
    if (profile?.full_name) {
      return profile.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <>
      <style>{`
        .shared-nav {
          position: sticky;
          top: 0;
          z-index: 9999;
          background: var(--bg-glass, rgba(244, 241, 235, 0.92));
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border-bottom: 1px solid var(--border-subtle, rgba(28, 25, 23, 0.10));
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
        }

        :root[data-theme="dark"] .shared-nav {
          background: var(--bg-glass, rgba(16, 14, 12, 0.88));
          border-bottom: 1px solid var(--border-subtle, rgba(240, 237, 230, 0.10));
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.25);
        }

        .shared-nav-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .shared-nav-logo {
          font-family: 'Roboto Slab', serif;
          font-weight: 900;
          font-size: 1.5rem;
          color: var(--text-primary, #1C1917);
          cursor: pointer;
          transition: color 0.2s;
        }

        :root[data-theme="dark"] .shared-nav-logo {
          color: var(--text-primary, #F0EDE6);
        }

        .shared-nav-logo:hover {
          color: var(--accent-primary, #D97642);
        }

        .shared-nav-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .shared-nav-btn {
          background: none;
          border: none;
          color: var(--text-primary, #1C1917);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 0.5rem;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        :root[data-theme="dark"] .shared-nav-btn {
          color: var(--text-primary, #F0EDE6);
        }

        .shared-nav-btn:hover {
          background: var(--accent-primary, #D97642);
          color: white;
        }

        .shared-nav-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--accent-primary, #D97642), #B85F2F);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: transform 0.2s;
          border: 2px solid var(--border-subtle, rgba(28, 25, 23, 0.10));
        }

        :root[data-theme="dark"] .shared-nav-avatar {
          border-color: var(--border-subtle, rgba(240, 237, 230, 0.10));
        }

        .shared-nav-avatar:hover {
          transform: scale(1.05);
          border-color: var(--accent-primary, #D97642);
        }

        .shared-nav-hamburger {
          background: none;
          border: 1px solid var(--border-subtle, rgba(28, 25, 23, 0.12));
          color: var(--text-primary, #1C1917);
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 0.5rem;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        :root[data-theme="dark"] .shared-nav-hamburger {
          border-color: var(--border-subtle, rgba(240, 237, 230, 0.12));
          color: var(--text-primary, #F0EDE6);
        }

        .shared-nav-hamburger:hover {
          background: var(--accent-primary, #D97642);
          color: white;
          border-color: var(--accent-primary, #D97642);
        }

        .shared-nav-dropdown {
          position: absolute;
          top: 100%;
          right: 1.5rem;
          margin-top: 0.5rem;
          background: var(--bg-secondary, #FDFCF8);
          border: 1px solid var(--border-subtle, rgba(28, 25, 23, 0.10));
          border-radius: 0.75rem;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
          min-width: 240px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          overflow: hidden;
        }

        :root[data-theme="dark"] .shared-nav-dropdown {
          background: var(--bg-secondary, #1A1714);
          border-color: var(--border-subtle, rgba(240, 237, 230, 0.10));
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
        }

        .shared-nav-dropdown.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .shared-nav-dropdown-header {
          padding: 1rem;
          border-bottom: 1px solid var(--border-subtle, rgba(28, 25, 23, 0.10));
        }

        :root[data-theme="dark"] .shared-nav-dropdown-header {
          border-bottom-color: var(--border-subtle, rgba(240, 237, 230, 0.10));
        }

        .shared-nav-dropdown-email {
          font-size: 0.875rem;
          color: var(--text-secondary, #6B6560);
          margin-top: 0.25rem;
        }

        :root[data-theme="dark"] .shared-nav-dropdown-email {
          color: var(--text-secondary, #9E9890);
        }

        .shared-nav-dropdown-name {
          font-weight: 600;
          color: var(--text-primary, #1C1917);
        }

        :root[data-theme="dark"] .shared-nav-dropdown-name {
          color: var(--text-primary, #F0EDE6);
        }

        .shared-nav-dropdown-section {
          padding: 0.5rem 0;
        }

        .shared-nav-dropdown-item {
          padding: 0.75rem 1rem;
          color: var(--text-primary, #1C1917);
          cursor: pointer;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          font-family: inherit;
          font-size: 0.9375rem;
        }

        :root[data-theme="dark"] .shared-nav-dropdown-item {
          color: var(--text-primary, #F0EDE6);
        }

        .shared-nav-dropdown-item:hover {
          background: var(--accent-primary, #D97642);
          color: white;
        }

        .shared-nav-dropdown-divider {
          height: 1px;
          background: var(--border-subtle, rgba(28, 25, 23, 0.10));
          margin: 0.5rem 0;
        }

        :root[data-theme="dark"] .shared-nav-dropdown-divider {
          background: var(--border-subtle, rgba(240, 237, 230, 0.10));
        }

        .shared-nav-auth-btns {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .shared-nav-btn-signin {
          padding: 0.65rem 1.1rem;
          background: transparent;
          border: 1px solid var(--border-subtle, rgba(28, 25, 23, 0.14));
          color: var(--text-primary, #1C1917);
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 600;
          font-size: 0.9375rem;
          opacity: 1;
        }

        :root[data-theme="dark"] .shared-nav-btn-signin {
          border-color: var(--border-subtle, rgba(240, 237, 230, 0.16));
          color: var(--text-primary, #F0EDE6);
        }

        .shared-nav-btn-signin:hover {
          border-color: var(--accent-primary, #D97642);
          background: var(--accent-primary, #D97642);
          color: white;
        }

        .shared-nav-btn-signup {
          padding: 0.65rem 1.1rem;
          background: var(--accent-primary, #D97642);
          border: 1px solid var(--accent-primary, #D97642);
          color: white;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          font-weight: 600;
          font-size: 0.9375rem;
          opacity: 1;
        }

        .shared-nav-btn-signup:hover {
          background: #C56838;
          border-color: #C56838;
          transform: translateY(-1px);
        }

        .shared-nav-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 9998;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s;
        }

        .shared-nav-backdrop.open {
          opacity: 1;
          visibility: visible;
        }

        @media (max-width: 768px) {
          .shared-nav-container {
            padding: 1rem;
          }

          .shared-nav-auth-btns {
            gap: 0.5rem;
          }

          .shared-nav-btn-signin,
          .shared-nav-btn-signup {
            padding: 0.65rem 0.9rem;
            font-size: 0.9rem;
          }

          .shared-nav-dropdown {
            right: 1rem;
            min-width: 200px;
          }
        }

        @media (max-width: 520px) {
          .shared-nav-logo {
            font-size: 1.35rem;
          }

          .shared-nav-right {
            gap: 0.5rem;
          }

          .shared-nav-auth-btns {
            gap: 0.5rem;
          }

          .shared-nav-btn-signin,
          .shared-nav-btn-signup {
            min-width: auto;
            white-space: nowrap;
          }
        }
      `}</style>

      <div
        className={`shared-nav-backdrop ${showMenu ? 'open' : ''}`}
        onClick={() => setShowMenu(false)}
      ></div>

      <nav className="shared-nav">
        <div className="shared-nav-container">
          <div className="shared-nav-logo" onClick={() => navigate('/')}>
            MIZAR
          </div>

          <div className="shared-nav-right">
            <button onClick={toggleTheme} className="shared-nav-btn" title="Toggle theme">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {user ? (
              <>
                <div className="shared-nav-avatar" title={profile?.full_name || user?.email}>
                  {getUserInitials()}
                </div>

                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="shared-nav-hamburger"
                  title="Menu"
                >
                  {showMenu ? <X size={20} /> : <Menu size={20} />}
                </button>

                <div className={`shared-nav-dropdown ${showMenu ? 'open' : ''}`}>
                  <div className="shared-nav-dropdown-header">
                    <div className="shared-nav-dropdown-name">
                      {profile?.full_name || 'User'}
                    </div>
                    <div className="shared-nav-dropdown-email">
                      {user?.email}
                    </div>
                  </div>

                  <div className="shared-nav-dropdown-section">
                    <button
                      onClick={() => handleNavigation('/console')}
                      className="shared-nav-dropdown-item"
                    >
                      Console
                    </button>

                    <button
                      onClick={() => handleNavigation('/implementation')}
                      className="shared-nav-dropdown-item"
                    >
                      Implementation
                    </button>

                    <button
                      onClick={() => handleNavigation('/account')}
                      className="shared-nav-dropdown-item"
                    >
                      Account Settings
                    </button>
                  </div>

                  <div className="shared-nav-dropdown-divider"></div>

                  <div className="shared-nav-dropdown-section">
                    <button
                      onClick={handleLogout}
                      className="shared-nav-dropdown-item"
                    >
                      <LogOut size={18} />
                      Sign Out
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="shared-nav-auth-btns">
                <button
                  onClick={() => navigate('/login')}
                  className="shared-nav-btn-signin"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="shared-nav-btn-signup"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}